// src/components/agent/parts/ChartsBlock.js
//
// ChartRenderer, extracted from MessageDisplay.js (frontend spec §2.3/§8.6):
// charts loaded from their stored file, hold-current-while-loading, the 5
// chart types, and combined charts rendered whole.
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import BarChartComponent from '../../charts/BarChart';
import LineChartComponent from '../../charts/LineChart';
import PieChartComponent from '../../charts/PieChart';
import BoxAndWhiskerChartComponent from '../../charts/BoxAndWhiskerChart';
import ScatterChartComponent from '../../charts/ScatterChart';
import SuperChartComponent from '../../charts/SuperChart';
import { fetchGcsObject } from '../../../services/api_utils';

function ChartsBlock({ chartGcsPath }) {
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartsRef = useRef(charts);

  useEffect(() => {
    chartsRef.current = charts;
  }, [charts]);

  useEffect(() => {
    let cancelled = false;

    const loadCharts = async () => {
      if (!chartGcsPath) {
        if (!chartsRef.current) {
          setError('No chart path provided.');
        }
        setLoading(false);
        return;
      }
      try {
        // Keep rendering the charts already shown while a new path loads.
        if (!chartsRef.current) {
          setLoading(true);
        }
        const chartData = await fetchGcsObject(chartGcsPath);
        if (!cancelled) {
          setCharts(chartData);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && !chartsRef.current) {
          setError('Failed to load chart data.');
        }
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [chartGcsPath]);

  if (loading && !charts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading chart...</Typography>
      </Box>
    );
  }

  if (error || !charts || !Array.isArray(charts)) {
    return <Typography color="error">Could not display chart. {error}</Typography>;
  }

  // The payload is a list (possibly nested lists) of charts. A combined chart
  // (`superchart`) is rendered as one — its title, its grouping and its order
  // — by the shared SuperChart renderer, never flattened into loose leaves.
  const flattenLists = (items) => (
    Array.isArray(items) ? items.flatMap(flattenLists) : [items]
  ).filter((item) => item && typeof item === 'object');

  const chartsToRender = flattenLists(charts);

  return (
    <>
      {chartsToRender.map((chartData, chartIndex) => (
        chartData.type === 'superchart' ? (
          <div key={`chart-${chartIndex}`} style={{ marginTop: '16px', width: '100%' }}>
            <SuperChartComponent chartData={chartData} />
          </div>
        ) : (
          <div key={`chart-${chartIndex}`} style={{ marginTop: '16px', width: '100%', height: '40vh', minHeight: '300px' }}>
            {chartData.type === 'bar' && <BarChartComponent chartData={chartData} />}
            {chartData.type === 'line' && <LineChartComponent chartData={chartData} />}
            {chartData.type === 'pie' && <PieChartComponent chartData={chartData} />}
            {chartData.type === 'box_and_whisker' && <BoxAndWhiskerChartComponent chartData={chartData} />}
            {chartData.type === 'scatter' && <ScatterChartComponent chartData={chartData} />}
          </div>
        )
      ))}
    </>
  );
}

export default ChartsBlock;
